import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useEffect, useState, useMemo} from 'react';
import {
  Home,
  FolderOpen,
  GraduationCap,
  Calendar,
  Upload,
  LogOut,
  HelpCircle,
  X,
  ChevronDown,
  ChevronRight,
  User,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {Input} from '@/shared/ui/input';
import {cn} from '@/lib/utils';
import {useAuth} from '@/contexts/AuthContext';
import {useToast} from '@/hooks/use-toast';
import {participantsApi} from '@/features/participants';
import {projectsApi} from '@/features/projects';
import {Project} from '@/shared/types/api';

interface ParticipantProjectNavigationProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

const ParticipantProjectNavigation = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}: ParticipantProjectNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {user, signOut} = useAuth();
  const {toast} = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsOpen, setProjectsOpen] = useState(() => {
    // Always start expanded on refresh/page load
    return true;
  });
  const [projectSearch, setProjectSearch] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(() => {
    // Always start with projects collapsed on refresh/page load
    return null;
  });

  // Wrapper function to handle projects section state and localStorage persistence
  const handleProjectsOpenChange = (isOpen: boolean) => {
    setProjectsOpen(isOpen);
    if (typeof window !== 'undefined') {
      localStorage.setItem('participant_sidebar_projects_open', isOpen.toString());
    }
  };

  // Wrapper function to handle both state update and localStorage persistence
  const handleExpandedProjectChange = (projectId: string | null) => {
    setExpandedProjectId(projectId);
    if (typeof window !== 'undefined') {
      if (projectId) {
        localStorage.setItem('participant_sidebar_expanded_project_id', projectId);
      } else {
        localStorage.removeItem('participant_sidebar_expanded_project_id');
      }
    }
  };

  // Filter projects based on search
  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [p?.name, p?.description, p?.status, p?.dynamics_id]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase())
        .some((s) => s.includes(q))
    );
  }, [projects, projectSearch]);

  // Effect to restore expanded state when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && expandedProjectId) {
      // Verify that the saved project ID still exists in the projects list
      const projectExists = projects.some(p => p.id === expandedProjectId);
      if (!projectExists) {
        handleExpandedProjectChange(null);
      } else {
      }
    }
  }, [projects]);

  // Auto-expand project when navigating to a project page
  useEffect(() => {
    const projectIdMatch = location.pathname.match(/\/participant\/project\/([^/]+)/);
    if (projectIdMatch && projectIdMatch[1]) {
      const projectId = projectIdMatch[1];
      if (projects.some(p => p.id === projectId) && expandedProjectId !== projectId) {
        handleExpandedProjectChange(projectId);
        handleProjectsOpenChange(true); // Also open the projects section
      }
    }
  }, [location.pathname, projects]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user?.email) return;

      try {
        // Get participant data
        const participantResponse = await participantsApi.getByEmail(user.email);
        if (!participantResponse.data || !participantResponse.data.project_id) {
          return;
        }

        // Handle both single project ID and array of project IDs
        const projectIds = Array.isArray(participantResponse.data.project_id) 
          ? participantResponse.data.project_id 
          : [participantResponse.data.project_id];

        // Fetch all projects in parallel
        const projectPromises = projectIds.map(async (projectId: string) => {
          try {
            const projectResponse = await projectsApi.getProject(projectId);
            if (projectResponse.data && projectResponse.data.data) {
              return projectResponse.data.data;
            }
            return null;
          } catch (error) {
            console.error(`Error fetching project ${projectId}:`, error);
            return null;
          }
        });

        const projectResults = await Promise.all(projectPromises);
        const validProjects = projectResults.filter(
          (project): project is Project => project !== null
        );

        // Filter for active projects
        const activeProjects = validProjects.filter(
          (project) => {
            const status = String(project.status || '').toLowerCase();
            return status === 'active';
          }
        );

        // Use active projects if available, otherwise use all valid projects
        const projectsToShow = activeProjects.length > 0 ? activeProjects : validProjects;
        setProjects(projectsToShow);

        // Restore expanded state if project exists
        if (expandedProjectId) {
          const projectExists = projectsToShow.some(p => p.id === expandedProjectId);
          if (!projectExists) {
            handleExpandedProjectChange(null);
          }
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, [user]);

  // Friendly display name helper
  const computeDisplayName = (u: any): string => {
    const md = u?.user_metadata || {};
    const fromMeta = md.full_name || md.name || md.display_name || '';
    if (fromMeta && typeof fromMeta === 'string') return fromMeta;
    const email: string = u?.email || '';
    if (!email) return '';
    const local = email.split('@')[0] || '';
    // Replace separators with spaces and capitalize each word
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: 'Signed Out',
        description: 'You have been successfully signed out',
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out',
        variant: 'destructive',
      });
    }
  };

  const getProjectSubPages = (projectId: string) => [
    {path: `/participant/project/${projectId}/overview`, icon: FolderOpen, label: 'Overview'},
    {path: `/participant/project/${projectId}/training`, icon: GraduationCap, label: 'Training'},
    {path: `/participant/project/${projectId}/schedule`, icon: Calendar, label: 'Schedule'},
    {path: `/participant/project/${projectId}/submit-data`, icon: Upload, label: 'Submit Data'},
  ];

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setIsMobileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsMobileMenuOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <nav
        className={cn(
          'fixed left-0 top-0 h-screen w-72 bg-card border-r border-border flex flex-col z-50 transition-transform duration-300',
          'lg:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className="p-4 border-b border-border relative">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full text-left rounded-xl p-3 flex items-center justify-between hover:bg-accent group">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const displayName = computeDisplayName(user as any);
                      const initials =
                        displayName
                          .split(/\s+/)
                          .filter(Boolean)
                          .map((s) => s[0]?.toUpperCase())
                          .slice(0, 2)
                          .join('') || 'U';
                      return (
                        <div className="h-9 w-9 rounded-full bg-muted text-foreground/80 flex items-center justify-center font-semibold">
                          {initials}
                        </div>
                      );
                    })()}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate flex items-center gap-1">
                        {computeDisplayName(user as any)}
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </div>
                    </div>
                  </div>
                </button>
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-3 py-2">
                <div className="text-sm font-medium truncate">
                  {computeDisplayName(user as any)}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  navigate('/participant/account-settings');
                  setIsMobileMenuOpen(false);
                }}
                className={`gap-2 ${
                  location.pathname === '/participant/account-settings'
                    ? '!bg-black !text-white dark:!bg-white dark:!text-black hover:!bg-black hover:!text-white dark:hover:!bg-white dark:hover:!text-black'
                    : ''
                }`}
              >
                <Settings className="h-4 w-4" /> Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  navigate('/participant/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`gap-2 ${
                  location.pathname === '/participant/profile'
                    ? '!bg-black !text-white dark:!bg-white dark:!text-black hover:!bg-black hover:!text-white dark:hover:!bg-white dark:hover:!text-black'
                    : ''
                }`}
              >
                <User className="h-4 w-4" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleSignOut();
                }}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" /> Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        {isMobileMenuOpen && (
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-lg lg:hidden hover:bg-accent absolute right-1 top-1"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {/* Home */}
            <Link
              to="/participant"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                location.pathname === '/participant'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              <Home className="h-4 w-4" />
              Home
            </Link>

            {/* Projects Section */}
            {projects.length > 0 && (
              <div className="mt-3">
                <button
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleProjectsOpenChange(!projectsOpen)}
                >
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" /> Projects
                  </span>
                  {projectsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {projectsOpen && (
                  <div className="mt-2 space-y-2">
                    <div className="px-3">
                      <Input
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        className="h-8"
                      />
                    </div>

                    <div className="pr-1">
                      {filteredProjects.map((p) => {
                        // Get proper status color based on project status
                        const getStatusDotColor = (status: string) => {
                          switch (status?.toLowerCase()) {
                            case 'active':
                              return 'bg-green-500';
                            case 'draft':
                              return 'bg-yellow-500';
                            case 'completed':
                              return 'bg-blue-500';
                            case 'inactive':
                              return 'bg-gray-400';
                            default:
                              return 'bg-gray-400';
                          }
                        };

                        const dotColor = getStatusDotColor(p.status || 'inactive');
                        const isExpanded = expandedProjectId === p.id;
                        const isProjectActive = location.pathname.startsWith(`/participant/project/${p.id}/`);

                        return (
                          <div key={p.id} className="">
                            <button
                              type="button"
                              onClick={() => {
                                // If project is not expanded, expand it and navigate to overview
                                if (!isExpanded) {
                                  handleExpandedProjectChange(p.id);
                                  navigate(`/participant/project/${p.id}/overview`);
                                  setIsMobileMenuOpen(false);
                                } else {
                                  // If already expanded, just collapse it
                                  handleExpandedProjectChange(null);
                                }
                              }}
                              className={cn(
                                "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground",
                                isProjectActive && "bg-accent"
                              )}
                            >
                              <span className="flex items-center gap-2 truncate">
                                <span
                                  className={`inline-block h-2.5 w-2.5 rounded-full ${dotColor}`}
                                />
                                <span className="truncate">{p.name}</span>
                              </span>
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 shrink-0" />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="ml-6 mt-1 mb-2 space-y-0.5">
                                {getProjectSubPages(p.id).map((item) => {
                                  const Icon = item.icon;
                                  const isActive = location.pathname === item.path;
                                  
                                  return (
                                    <Link
                                      key={item.path}
                                      to={item.path}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className={cn(
                                        'flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs',
                                        isActive
                                          ? 'bg-black text-white dark:bg-white dark:text-black'
                                          : 'hover:bg-accent hover:text-accent-foreground'
                                      )}
                                    >
                                      <Icon className="h-3 w-3" /> {item.label}
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {filteredProjects.length === 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          No projects found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* My Calendar - Hidden per user request for all roles */}
            {/* eslint-disable-next-line no-constant-binary-expression */}
            {false && (
            <Link
              to="/participant/calendar"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                location.pathname === '/participant/calendar'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              <Calendar className="h-4 w-4" />
              My Calendar
            </Link>
            )}

            {/* Support/FAQ - Hidden per user request for all roles */}
            {/* eslint-disable-next-line no-constant-binary-expression */}
            {false && (
            <Link
              to="/participant/support"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                location.pathname === '/participant/support'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              )}
            >
              <HelpCircle className="h-4 w-4" />
              Support/FAQ
            </Link>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default ParticipantProjectNavigation;

